import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Truck } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_person: "", phone: "", email: "", address: "", payment_terms: "", is_active: true };

export default function Suppliers() {
  const { can } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [perf, setPerf] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/suppliers/performance").then((r) => setPerf(Object.fromEntries(r.data.map((x) => [x.supplier_id, x.fill_rate])))).catch(() => {});
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); };
  const save = async () => {
    try { if (editId) await api.put(`/suppliers/${editId}`, form); else await api.post("/suppliers", form); toast.success("Supplier saved"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Vendors supplying your pharmacy network"
        actions={can("procurement.manage") && <Button data-testid="add-supplier-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Supplier</Button>} />
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Supplier</th><th className="py-2.5 px-3">Contact</th><th className="py-2.5 px-3">Phone</th>
            <th className="py-2.5 px-3">Terms</th><th className="py-2.5 px-3 text-right">Fill rate</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {suppliers.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />No suppliers</td></tr>}
            {suppliers.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-muted/50">
                <td className="py-2 px-3 font-medium text-foreground">{s.name}<div className="text-xs text-muted-foreground">{s.email}</div></td>
                <td className="py-2 px-3 text-muted-foreground">{s.contact_person || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{s.phone ? <a href={`tel:${s.phone}`} className="text-sky-600 hover:underline" data-testid={`call-supplier-${s.id}`}>{s.phone}</a> : "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{s.payment_terms || "—"}</td>
                <td className="py-2 px-3 text-right font-mono text-xs">{perf[s.id] != null ? `${perf[s.id]}%` : "—"}</td>
                <td className="py-2 px-3"><StatusBadge value={s.is_active === false ? "out" : "ok"} label={s.is_active === false ? "Inactive" : "Active"} /></td>
                <td className="py-2 px-3 text-right">{can("procurement.manage") && <Button variant="ghost" size="sm" data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="supplier-name-input" value={form.name} onChange={set("name")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            </div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
            <div><Label>Payment terms</Label><Input placeholder="Net 30" value={form.payment_terms} onChange={set("payment_terms")} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="supplier-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-supplier-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
